// src/constants/chatToolLabels.js
// What the tool status pills in patient chat say, and how long they stay.
//
// One copy, imported by both the hook that creates the pills (useChat.js) and
// the component that renders them (MessageBubble.jsx). They each used to carry
// their own copy of this map, which is how a pill could be created for a tool
// the renderer had no label for.
//
// KEYED BY ACTION, NOT BY TOOL
// ----------------------------
// The chat tools are multi-action: `update_profile` also reads the profile and
// the saved addresses, `order_lab_test` also lists the catalogue and searches
// collection slots, `book_appointment` also lists doctors. One label per tool
// meant the label was wrong for every action but one — a patient asking for a
// lab test was told "Details saved" while their profile was merely being READ,
// and "Home collection details ready ✓" sat directly above "no slots are
// available on 2026-08-23". Claiming a save that never happened, and a success
// that failed, is worse than saying nothing at all.
//
// So the key is `tool:action` where the tool has actions, and `tool` where it
// doesn't. See toolRouter.js for the authoritative action list per tool.
//
// A PILL IS A WAIT INDICATOR, NOT A RECEIPT
// -----------------------------------------
// Its job is to cover the latency of a step the patient can't see. Once the
// answer arrives, the answer IS the receipt: "Panel details ready ✓" tells a
// patient nothing that the panel list right below it doesn't already say, and
// leaving one behind per step means a long conversation accumulates a column of
// green ticks competing with the actual content.
//
// So a pill retires when its step finishes — EXCEPT:
//   `persist: true` — a write with no other visible receipt. The patient's
//   record changed and nothing else on screen says so, so the pill stays, with
//   a doneLabel naming what was written. Writes that DO produce their own
//   receipt (create_order and create_appointment both render a payment card)
//   retire like any read.
//   A failed step always stays, whatever it was — a step that silently vanishes
//   is a step the patient thinks succeeded.

export const TOOL_STATUS = {
  // ── book_appointment ────────────────────────────────────────────────────
  'book_appointment:list_doctors':        { emoji: '👩‍⚕️', label: 'Looking up doctors…' },
  'book_appointment:get_appointments':    { emoji: '📅', label: 'Checking your appointments…' },
  'book_appointment:get_available_slots': { emoji: '📅', label: 'Checking available slots…' },
  // Receipt is the appointment payment card.
  'book_appointment:create_appointment':  { emoji: '📅', label: 'Booking your appointment…',
                                            errorLabel: 'Appointment not booked' },

  // ── book_navigator_appointment ──────────────────────────────────────────
  // confirm books immediately — no payment card, so (like update_profile's
  // writes) it needs `persist` since nothing else on screen shows the write.
  'book_navigator_appointment:get_available_slots':
    { emoji: '📅', label: "Checking your navigator's availability…" },
  'book_navigator_appointment:confirm':
    { emoji: '📅', label: 'Booking with your navigator…',
      doneLabel: 'Appointment booked', errorLabel: 'Appointment not booked', persist: true },

  // ── order_lab_test ──────────────────────────────────────────────────────
  'order_lab_test:list_catalog':          { emoji: '🧪', label: 'Looking up available tests…' },
  'order_lab_test:get_package_details':   { emoji: '🧪', label: 'Getting the package details…' },
  'order_lab_test:search_slots':          { emoji: '🏠', label: 'Checking home collection slots…' },
  // Receipt is the lab test payment card.
  'order_lab_test:create_order':          { emoji: '🏠', label: 'Arranging your home collection…',
                                            errorLabel: 'Home collection not arranged' },

  // ── update_profile ──────────────────────────────────────────────────────
  // The three writes here are the reason `persist` exists: nothing else on
  // screen tells the patient their own record was changed.
  'update_profile:get_profile':           { emoji: '👤', label: 'Checking your profile…' },
  'update_profile:get_addresses':         { emoji: '📍', label: 'Checking your saved addresses…' },
  'update_profile:update_profile':        { emoji: '✓',  label: 'Saving your details…',
                                            doneLabel: 'Details saved',
                                            errorLabel: 'Details not saved', persist: true },
  'update_profile:update_address':        { emoji: '📍', label: 'Saving your address…',
                                            doneLabel: 'Address saved',
                                            errorLabel: 'Address not saved', persist: true },
  'update_profile:add_address':           { emoji: '📍', label: 'Adding your address…',
                                            doneLabel: 'Address added',
                                            errorLabel: 'Address not added', persist: true },

  // ── single-action tools ─────────────────────────────────────────────────
  'show_marker_trend':                    { emoji: '📊', label: 'Pulling up your test history…' },
  'get_panel_pricing':                    { emoji: '💡', label: 'Getting your panel details…' },
  'download_triage_report':               { emoji: '📄', label: 'Preparing your report…' },
  // `silent`: questionnaire answers are captured quietly from the conversation
  // — no "Saving…" or "Answers saved" pill, so the chat doesn't read like a
  // form being filled in. Only a FAILED save is shown (see ToolStatusCard).
  'submit_symptom_answers':               { emoji: '📝', label: 'Saving your answers…',
                                            errorLabel: 'Answers not saved', silent: true },
  'add_navigator_note':                   { emoji: '📝', label: 'Sending your note to your navigator…',
                                            doneLabel: 'Note sent to your navigator',
                                            errorLabel: 'Note not sent', persist: true },
};

// Shown when a tool (or a new action on a known tool) has no entry above.
// Never the raw tool name: `submit_symptom_answers` had no label at all and the
// old fallback rendered that string, snake_case and all, to a patient.
export const GENERIC_TOOL_STATUS = { emoji: '⚙️', label: 'Working on it…' };

// What a failed step reads as when its entry names nothing more specific.
export const GENERIC_ERROR_LABEL = "That step didn't go through";

/**
 * The pill definition for one tool call. `action` comes from the tool input
 * (toolRouter.js dispatches on it); tools without actions ignore it.
 * Falls back to the tool-level entry, then to the generic one, so an action
 * added to toolRouter without a label here still reads as English.
 */
export function resolveToolStatus(toolName, action) {
  return (action && TOOL_STATUS[`${toolName}:${action}`])
    || TOOL_STATUS[toolName]
    || GENERIC_TOOL_STATUS;
}

/** Does this step's pill stay on screen once it has finished successfully? */
export function persistsWhenDone(toolName, action) {
  return Boolean(resolveToolStatus(toolName, action).persist);
}

export default TOOL_STATUS;
